"use client";

import React, { useEffect, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import { ColletteIconButton } from "@collette-travel/collette-common-controls";
import { Close } from "@mui/icons-material";
import RequestABrochureSubmissionMessage from "@/components/forms/request-a-brochure/request-a-brochure-submission-message";
import HubspotFormDisclaimer from "@/components/forms/disclaimer/hubspot-form-disclaimer";
import { useForm } from "react-hook-form";
import RequestABrochureForm from "@/components/forms/request-a-brochure/request-a-brochure-form";
import { Brochure, BrochureDisplay } from "@/types/dto/common/brochure";
import DataLayer from "@/components/data-layer/data-layer";
import rabDataLayerPushFactory from "@/factories/data-layer/rab-data-layer-push-factory";
// eslint-disable-next-line import/no-named-as-default
import useUserGroups from "@/hooks/login/useUserGroups";
import { useMarketCurrency } from "@/providers/market-currency-provider";
import { Locale } from "@/i18n/routing";
import { locale2Country } from "@/config/country-codes";
import useRABSubmit from "@/hooks/forms/on-submit/use-rab-submit";
import { RABFormData } from "@/types/leads/lead-forms";
import AdvisorBrochureForm from "./request-advisor-brochure-form";

interface RequestABrochureProps {
  handleClose: () => void;
  brochureDisplay: BrochureDisplay;
  isLandingPage?: boolean;
  preSelectedBrochure?: Brochure;
  preSelectedBrochures?: Brochure[];
  requireOnlyMailingAddress?: boolean;
  onSelectionChange?: React.Dispatch<React.SetStateAction<Brochure[]>>;
  defaultValues?: any;
}

const RequestABrochureSection = ({
  handleClose,
  brochureDisplay,
  isLandingPage = false,
  preSelectedBrochure,
  preSelectedBrochures,
  requireOnlyMailingAddress = false,
  onSelectionChange: setSelectedBrochures,
  defaultValues
}: RequestABrochureProps) => {
  const t = useTranslations("FORMS.RAB");
  const locale = useLocale();
  const sourceUrl = window.location.href;

  const { isAgent } = useUserGroups();

  const { marketId, currencyId } = useMarketCurrency();

  const localeSpecificDefaults = locale2Country(locale as Locale);

  const {
    handleSubmit,
    formState: { isSubmitSuccessful, errors, isValid, isSubmitted, isSubmitting },
    control,
    getValues,
    trigger,
    setValue,
    watch
  } = useForm<RABFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      customerLeadBrochures:
        preSelectedBrochures ?? (preSelectedBrochure ? [preSelectedBrochure] : []),
      title: defaultValues?.title ?? "",
      firstName: defaultValues?.firstName ?? "",
      lastName: defaultValues?.lastName ?? "",
      email: defaultValues?.email ?? "",
      confirmEmail: defaultValues?.confirmEmail ?? "",
      countryCode: localeSpecificDefaults,
      phone: defaultValues?.phone ?? "",
      address: defaultValues?.address ?? "",
      address2: defaultValues?.address2 ?? "",
      country: localeSpecificDefaults.name,
      state: defaultValues?.state ?? "",
      city: defaultValues?.city ?? "",
      zipCode: defaultValues?.zipCode ?? "",
      isTravelAgent: defaultValues?.isTravelAgent ?? "",
      optInEmail: locale === "en-us",
      withMoreTravelers: false,
      recaptchaToken: undefined,
      experianAddress: {
        suggestion: defaultValues?.experianAddress?.suggestion ?? "",
        id: defaultValues?.experianAddress?.id ?? ""
      }
    }
  });

  const customerLeadBrochures = watch("customerLeadBrochures");

  useEffect(() => {
    setSelectedBrochures?.(customerLeadBrochures);
  }, [customerLeadBrochures]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSubmitSuccessful) {
      setSelectedBrochures?.([]);
    }
  }, [isSubmitSuccessful]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataLayerPush = useMemo(
    () => rabDataLayerPushFactory(getValues(), sourceUrl, isAgent.toString()),
    [getValues, isAgent, sourceUrl, isSubmitSuccessful]
  ); // necessary dependency isSubmitted

  const { onSubmit, serverError } = useRABSubmit({
    locale,
    sourceUrl,
    isLandingPage,
    marketId,
    currencyId
  });

  const onError = async () => {
    await trigger();
    console.log("ERRORS", errors);
  }; 

  useEffect(() => {
    if (preSelectedBrochures && requireOnlyMailingAddress) {
      setValue("customerLeadBrochures", preSelectedBrochures);
    }
  }, [preSelectedBrochures]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack
      sx={{
        width: "100%",
        borderRadius: "1rem",
        backgroundColor: "background.default",
        padding: { xs: "2rem 2.4rem", md: "4rem 4rem 0" }
        
      }}
    >
      {/* Header */}
      <Stack direction="row" gap="1rem" alignItems="flex-start" justifyContent={isSubmitSuccessful ? "end" : "space-between"}>
        {!isSubmitSuccessful && (
        <Typography variant="h4" mb="1.2rem" data-testid="rab-header">
          {requireOnlyMailingAddress ? t("Order Brochures") : t("Request a Brochure")}
        </Typography>
        )}
        {!requireOnlyMailingAddress && (
          <ColletteIconButton
            testId="rab-close-button"
            onClick={handleClose}
            sx={{
              mt: "-0.8rem",
              mr: "-0.8rem"
            }}
          >
            <Close />
          </ColletteIconButton>
        )}
      </Stack>
      {/* Form and submission response */}
      {isSubmitSuccessful && (
        <RequestABrochureSubmissionMessage
          error={serverError}
          numBrochures={getValues("customerLeadBrochures").length}
        />
      )}
      {!isSubmitSuccessful && !requireOnlyMailingAddress && (
        <RequestABrochureForm
          control={control}
          handleSubmit={handleSubmit(onSubmit, onError)}
          brochureDisplay={brochureDisplay}
          isInvalid={!isValid && isSubmitted}
          isSubmitting={isSubmitting}
        />
      )}
      {!isSubmitSuccessful && requireOnlyMailingAddress && (
        <AdvisorBrochureForm
          control={control}
          handleSubmit={handleSubmit(onSubmit, onError)}
          brochureDisplay={brochureDisplay}
          isInvalid={!isValid && isSubmitted}
          isSubmitting={isSubmitting}
          setValue={setValue}
          defaultValues={defaultValues}
        />
      )}
      {/* Privacy Policy */}
      {!isSubmitSuccessful && !requireOnlyMailingAddress && (
        <HubspotFormDisclaimer testId="rab-disclaimer" />
      )}
      {isSubmitSuccessful && <DataLayer data={dataLayerPush} />}
    </Stack>
  );
};

export default RequestABrochureSection;
